import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

export interface InboxReplyDraft {
  body: string;
  important: boolean;
  requestId?: string;
  requestFingerprint?: string;
  /** The lesson selected for this draft; null records an explicit removal. */
  lessonId?: string | null;
  /** Route lesson context at the time the draft choice was saved. */
  lessonContextId?: string | null;
}

interface InboxReplyDraftState {
  get: (threadId: string) => InboxReplyDraft | null;
  save: (threadId: string, draft: InboxReplyDraft) => void;
  clear: (threadId: string) => void;
}

const EMPTY_STATE: InboxReplyDraftState = {
  get: () => null,
  save: () => {},
  clear: () => {},
};

const InboxReplyDraftContext = createContext<InboxReplyDraftState>(EMPTY_STATE);

/**
 * Reply text stays in account-shell memory: it survives route changes without
 * appearing in a URL or browser storage. Key the provider to the account so a
 * sign-out or account switch discards the previous account's drafts.
 */
export function InboxReplyDraftProvider({ children }: { children: ReactNode }) {
  const [drafts, setDrafts] = useState<Record<string, InboxReplyDraft>>({});

  const get = useCallback((threadId: string) => drafts[threadId] ?? null, [drafts]);
  const save = useCallback((threadId: string, draft: InboxReplyDraft) => {
    setDrafts((current) => {
      if (draft.body === "" && !draft.important) {
        if (!(threadId in current)) return current;
        const next = { ...current };
        delete next[threadId];
        return next;
      }
      return { ...current, [threadId]: draft };
    });
  }, []);
  const clear = useCallback((threadId: string) => {
    setDrafts((current) => {
      if (!(threadId in current)) return current;
      const next = { ...current };
      delete next[threadId];
      return next;
    });
  }, []);

  const value = useMemo(() => ({ get, save, clear }), [get, save, clear]);
  return <InboxReplyDraftContext.Provider value={value}>{children}</InboxReplyDraftContext.Provider>;
}

export function useInboxReplyDrafts(): InboxReplyDraftState {
  return useContext(InboxReplyDraftContext);
}
